// packages/ui/src/sidebar/sidebarStore.ts
import { create } from 'zustand';

const OPEN_KEY = 'slate:sidebarOpen';
const PANEL_KEY = 'slate:sidebarPanel';
const MAIN_VIEW_KEY = 'slate:mainView';
const LEGACY_COLLAPSED_KEY = 'slate:sidebarCollapsed';
const DEFAULT_PANEL = 'explorer';

function loadOpen(): boolean {
  const ls = globalThis.localStorage;
  if (!ls) return true;
  const v = ls.getItem(OPEN_KEY);
  if (v !== null) return v === '1';
  // Migrate the legacy collapsed flag: open = !collapsed.
  return ls.getItem(LEGACY_COLLAPSED_KEY) !== '1';
}

function loadPanel(): string {
  return globalThis.localStorage?.getItem(PANEL_KEY) ?? DEFAULT_PANEL;
}

function loadMainView(): string | null {
  return globalThis.localStorage?.getItem(MAIN_VIEW_KEY) ?? null;
}

interface SidebarState {
  /** Expanded vs collapsed-to-rail. The activity bar stays visible either way. */
  open: boolean;
  /** Which registered panel renders in the sidebar host. */
  activePanelId: string;
  /**
   * The active full-editor-area main view (e.g. the Git client), or null when
   * the normal editor area is shown. Persisted separately from the sidebar
   * panel: a main view replaces the editor area without touching the sidebar.
   */
  activeMainView: string | null;
  /**
   * Toggle (or set) the open state. Re-opening the sidebar (open === true) is
   * mutually exclusive with a main view, so it also closes any active one.
   */
  toggleOpen: (open?: boolean) => void;
  /** Select a panel; always opens the sidebar and closes any active main view. */
  setActivePanel: (id: string) => void;
  /**
   * Open a main view (id) over the editor area, or close it (null). Opening one
   * forces the sidebar closed: the main view and the sidebar panel are one
   * mutually-exclusive activity-bar selection group.
   */
  setActiveMainView: (id: string | null) => void;
}

export const useSidebarStore = create<SidebarState>((set, get) => ({
  open: loadOpen(),
  activePanelId: loadPanel(),
  activeMainView: loadMainView(),
  toggleOpen: (open) => {
    const next = open ?? !get().open;
    const ls = globalThis.localStorage;
    ls?.setItem(OPEN_KEY, next ? '1' : '0');
    // Re-opening the sidebar is exclusive with a main view, so close any active
    // one. Collapsing (next === false) leaves the main view alone.
    if (next) {
      ls?.removeItem(MAIN_VIEW_KEY);
      set({ open: next, activeMainView: null });
    } else {
      set({ open: next });
    }
  },
  setActivePanel: (id) => {
    const ls = globalThis.localStorage;
    ls?.setItem(PANEL_KEY, id);
    ls?.setItem(OPEN_KEY, '1');
    // Opening a sidebar panel closes any active main view (mutually exclusive).
    ls?.removeItem(MAIN_VIEW_KEY);
    set({ activePanelId: id, open: true, activeMainView: null });
  },
  setActiveMainView: (id) => {
    const ls = globalThis.localStorage;
    if (id === null) {
      ls?.removeItem(MAIN_VIEW_KEY);
      set({ activeMainView: id });
    } else {
      // A main view takes over the editor area and hides the sidebar panel.
      ls?.setItem(MAIN_VIEW_KEY, id);
      ls?.setItem(OPEN_KEY, '0');
      set({ activeMainView: id, open: false });
    }
  },
}));
